// A placeholder land: a plain disc of ground in the land's colour with a name sign and a few rocks for scale. It stands in for a land
// until that land's real art exists (then write src/scene/lands/<id>.js and register it in lands/index.js).
//
// Every land builder has this shape — async build(def, progress) →
//   { root, spawn: {x,y,z}, walk(wx, wz, wy) → floorY | null, tick?(dt, hx, hz) }
// root is added to the scene by landScene (userData.isBiome keeps the home-hiding code from touching it); spawn is where the cat
// appears; walk() is the hero's walking rule inside the land (a floor height, or null = can't stand there).
import * as THREE from 'three'
import { GROUND_Y } from '../hexGrid.js'
import { LandPlaceholder } from '../../config/destinations.js'

function nameSign(text, sub = 'preview — an empty land for now') {
  const cv = document.createElement('canvas')
  cv.width = 512; cv.height = 128
  const c = cv.getContext('2d')
  c.fillStyle = 'rgba(20, 20, 18, 0.78)'; c.beginPath(); c.roundRect(4, 4, 504, 120, 18); c.fill()
  c.strokeStyle = 'rgba(233, 226, 207, 0.35)'; c.lineWidth = 3; c.stroke()
  c.fillStyle = '#f1e8d0'; c.textAlign = 'center'; c.textBaseline = 'middle'
  c.font = '700 40px Inter, system-ui, sans-serif'; c.fillText(text, 256, 50)
  c.font = '500 22px Inter, system-ui, sans-serif'; c.fillStyle = '#cabf99'; c.fillText(sub, 256, 94)
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }))
  sp.scale.set(3.6, 0.9, 1)
  return sp
}

// a small repeatable pseudo-random (the same land always looks the same)
const seeded = (seed) => { let s = seed >>> 0; return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296) }

export async function buildPlaceholderLand(def, progress = () => {}) {
  const R = LandPlaceholder.radiusUnits
  const root = new THREE.Group()
  root.userData.isBiome = true
  root.userData.landId = def.id
  root.visible = false

  const ground = new THREE.Mesh(new THREE.CircleGeometry(R, 72).rotateX(-Math.PI / 2), new THREE.MeshLambertMaterial({ color: def.ground ?? 0x7a8a6a }))
  ground.position.y = GROUND_Y - 0.02
  root.add(ground)
  const rim = new THREE.Mesh(new THREE.RingGeometry(R - 0.5, R, 72).rotateX(-Math.PI / 2), new THREE.MeshLambertMaterial({ color: 0xf1e8d0, transparent: true, opacity: 0.25 }))
  rim.position.y = GROUND_Y - 0.01
  root.add(rim)
  progress(0.4)

  const rnd = seeded([...def.id].reduce((a, ch) => a * 31 + ch.charCodeAt(0), 7))
  const rockMat = new THREE.MeshLambertMaterial({ color: 0x6c6a63, flatShading: true })
  for (let i = 0; i < 22; i++) {
    const a = rnd() * Math.PI * 2, d = 5 + rnd() * (R - 8), s = 0.25 + rnd() * 0.55
    const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(s, 0), rockMat)
    rock.position.set(Math.cos(a) * d, GROUND_Y + s * 0.4, Math.sin(a) * d)
    rock.rotation.set(rnd() * 3, rnd() * 3, rnd() * 3)
    root.add(rock)
  }
  progress(0.8)

  const sign = nameSign(def.name, def.signSub)
  sign.position.set(0, GROUND_Y + 1.7, -3.2)
  root.add(sign)
  progress(1)

  const limit = R - 0.8
  return {
    root,
    spawn: { x: 0, y: GROUND_Y, z: 0 },
    walk: (wx, wz) => (Math.hypot(wx, wz) <= limit ? GROUND_Y : null),
  }
}
